/** Default upsell footer. Variant slot props (`ctaSubtitleSlot`,
 * `trustSignalSlot`, `guaranteeBadgeSlot`) are opaque `ReactNode`s so
 * variant components live in their own files, not as inline conditionals. */

import React, { ReactNode } from 'react';

import { Body as BodyText } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { WorkId } from 'js/liswp/types';

import {
  Footer,
  NoThanksButton,
  PrimaryButtonWrap,
} from './StatelessWorkCardsUpsellModal.styles';

export interface FooterImplProps {
  isPremium: boolean;
  hideSecondaryAction: boolean;
  onSecondaryAction?: () => void;
  onClose: () => void;
  secondaryActionLabel?: string;
  onPrimaryCtaClicked: () => void;
  primaryCtaLabel: string;
  primaryCtaImpressionSource: string;
  sourceWorkId: WorkId;
  modalVariant: string;
  transactionalPricingBucket?: string;
  /** Rendered under the primary CTA. */
  ctaSubtitleSlot?: ReactNode;
  /** Rendered below the primary CTA. */
  trustSignalSlot?: ReactNode;
  /** Rendered below the primary CTA. */
  guaranteeBadgeSlot?: ReactNode;
}

export function DefaultModalFooter({
  isPremium,
  hideSecondaryAction,
  onSecondaryAction,
  onClose,
  secondaryActionLabel,
  onPrimaryCtaClicked,
  primaryCtaLabel,
  primaryCtaImpressionSource,
  sourceWorkId,
  modalVariant,
  transactionalPricingBucket,
  ctaSubtitleSlot = null,
  trustSignalSlot = null,
  guaranteeBadgeSlot = null,
}: FooterImplProps) {
  const handleSecondary = onSecondaryAction ?? onClose;
  const renderedSecondaryLabel =
    secondaryActionLabel ?? "No thanks, I'll stay on the free tier";
  const ctaImpressionExtra = {
    modalVariant,
    is_premium: isPremium,
    ...(transactionalPricingBucket !== undefined && {
      transactional_pricing_bucket: transactionalPricingBucket,
    }),
  };

  return (
    <Footer className={isPremium ? 'premium' : undefined}>
      {!hideSecondaryAction && (
        <NoThanksButton type="button" onClick={handleSecondary}>
          <BodyText size="md" as="span">
            {renderedSecondaryLabel}
          </BodyText>
        </NoThanksButton>
      )}
      <ImpressionWatcher
        source={primaryCtaImpressionSource}
        entity_type={ImpressionEntity.Work}
        entity_id={sourceWorkId}
        extra={ctaImpressionExtra}
      >
        <PrimaryButtonWrap>
          <Button
            size="lg"
            iconRight={isPremium ? 'download' : 'arrow_forward'}
            fullWidth
            onClick={onPrimaryCtaClicked}
          >
            {primaryCtaLabel}
          </Button>
          {guaranteeBadgeSlot}
          {trustSignalSlot}
          {ctaSubtitleSlot}
        </PrimaryButtonWrap>
      </ImpressionWatcher>
    </Footer>
  );
}

export default DefaultModalFooter;
